import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSuggestMeals } from '@/hooks/useAi';
import { useCreateMeal } from '@/hooks/useMeals';
import { useToast } from '@/components/ui/ToastProvider';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import type { AiMealSuggestion } from '@/types/api';

export const Route = createFileRoute('/meals/enrich')({
  beforeLoad: requireOrganizer,
  component: MealsEnrichComponent,
});

function MealsEnrichComponent() {
  const { t } = useTranslation();
  const toast = useToast();
  const suggestMutation = useSuggestMeals();
  const createMealMutation = useCreateMeal();

  const [context, setContext] = useState('');
  const [suggestions, setSuggestions] = useState<AiMealSuggestion[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [accepted, setAccepted] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const abortRef = useRef<AbortController | null>(null);

  const handleGenerate = () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSuggestions([]);
    setDismissed(new Set());
    setAccepted(new Set());

    suggestMutation.mutate(
      { count: 8, context: context.trim() || undefined },
      {
        onSuccess: (data) => setSuggestions(data.suggestions),
        onError: (err: unknown) => {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 503) toast.error(t('enrich.errorUnavailable'));
          else if (status === 401 || status === 403) toast.error(t('enrich.errorAuth'));
          else toast.error(t('enrich.error'));
        },
      },
    );
  };

  const handleAccept = async (idx: number) => {
    const s = suggestions[idx];
    setSaving((prev) => new Set(prev).add(idx));
    try {
      await createMealMutation.mutateAsync({
        name: s.name,
        tags: s.tags.map((tag) => ({ tag, isHidden: false })),
      });
      setAccepted((prev) => new Set(prev).add(idx));
      toast.success(t('enrich.mealAdded', { name: s.name }));
    } catch {
      toast.error(t('enrich.saveFailed', { name: s.name }));
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(idx);
        return next;
      });
    }
  };

  const handleDismiss = (idx: number) => {
    setDismissed((prev) => new Set(prev).add(idx));
  };

  const visibleSuggestions = suggestions.filter(
    (_, i) => !dismissed.has(i) && !accepted.has(i),
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/meals"
            className="text-sm text-blue-600 hover:text-blue-700 mb-4 inline-block"
          >
            ← {t('meals.backToMeals')}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">{t('enrich.title')}</h1>
          <p className="mt-2 text-gray-600">{t('enrich.description')}</p>
        </div>

        {/* Context input + generate button */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('enrich.contextLabel')}
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
              placeholder={t('enrich.contextPlaceholder')}
              className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleGenerate}
              disabled={suggestMutation.isPending}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {suggestMutation.isPending ? t('enrich.generating') : t('enrich.generate')}
            </button>
          </div>
        </div>

        {/* Loading */}
        {suggestMutation.isPending && (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent" />
            <p className="mt-4 text-gray-600">{t('enrich.generating')}</p>
          </div>
        )}

        {/* Stats bar */}
        {suggestions.length > 0 && !suggestMutation.isPending && (
          <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
            <span>
              {t('enrich.stats', {
                accepted: accepted.size,
                dismissed: dismissed.size,
                remaining: visibleSuggestions.length,
              })}
            </span>
          </div>
        )}

        {/* Suggestions grid */}
        {visibleSuggestions.length > 0 && (
          <div className="space-y-3">
            {suggestions.map((s, idx) => {
              if (dismissed.has(idx) || accepted.has(idx)) return null;
              const isSaving = saving.has(idx);
              return (
                <div
                  key={idx}
                  className="bg-white rounded-lg border border-gray-200 p-4 flex items-start gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{s.name}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {s.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            tag === 'healthy'
                              ? 'bg-green-100 text-green-700'
                              : tag === 'cheat'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    {s.justification && (
                      <p className="text-xs text-gray-500 mt-1.5 italic">{s.justification}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => handleAccept(idx)}
                      disabled={isSaving}
                      className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {isSaving ? '...' : t('enrich.accept')}
                    </button>
                    <button
                      onClick={() => handleDismiss(idx)}
                      disabled={isSaving}
                      className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
                    >
                      {t('enrich.dismiss')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state after all processed */}
        {suggestions.length > 0 && visibleSuggestions.length === 0 && !suggestMutation.isPending && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">{t('enrich.allProcessed')}</p>
            <p className="text-sm mt-1">{t('enrich.generateMore')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
